import { motion } from 'framer-motion'
import { t } from 'i18next'
import React from 'react'
import { useForm } from 'react-hook-form'
import { useEtracker } from '../../../hooks/use-etracker'
import { Button } from '../../button/button'
import { RadioButtonGroup } from '../../radio-button-group/radio-button-group'
import { TextArea } from '../../text-area/text-area'
import styles from '../exit-feedback-modal.module.css'

interface InitialExitFormProps {
	onSubmit: () => void
	exitButtonSlot?: React.ReactNode
}

type ExitReasons = 'justCurious' | 'noCV' | 'wrongCVFormat' | 'other'

export const InitialExitForm: React.FC<InitialExitFormProps> = ({ onSubmit, exitButtonSlot }) => {
	const {
		register,
		handleSubmit,
		watch,
		control,
		formState: { errors },
	} = useForm()
	const { trackExitEvent } = useEtracker()

	const watchedExitReason = watch('exit-reason')

	const options: {
		value: ExitReasons
		label: string
		append?: React.ReactNode
	}[] = [
		{ value: 'justCurious', label: t('exitFeedback.formConsentCheck.reason.options.justCurious') },
		{ value: 'noCV', label: t('exitFeedback.formConsentCheck.reason.options.noCV') },
		{ value: 'wrongCVFormat', label: t('exitFeedback.formConsentCheck.reason.options.wrongCVFormat') },
		{
			value: 'other',
			label: t('exitFeedback.formConsentCheck.reason.options.other'),
			append: (
				<motion.div
					animate={{
						opacity: watchedExitReason === 'other' ? 1 : 0,
						height: watchedExitReason === 'other' ? 'auto' : '0px',
					}}
					transition={{ duration: 0.3, ease: 'easeInOut' }}
					style={{ overflow: 'hidden' }}
				>
					<TextArea
						control={control}
						name={'other-explanation'}
						registerOptions={{ required: t('exitFeedback.fieldRequired') }}
						placeholder={t('exitFeedback.formConsentCheck.reason.options.otherPlaceholder')}
						disabled={watchedExitReason !== 'other'}
						error={errors['other-explanation'] && t('exitFeedback.fieldRequired')}
					/>
				</motion.div>
			),
		},
	]

	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	const onFormSubmit = (data: any) => {
		const exitValue = data['exit-reason']
		const translatedExitValue = options.find((option) => option.value === exitValue)?.label || exitValue

		const answer = `${translatedExitValue}${data['other-explanation'] ? `:${data['other-explanation']}` : ''}`

		if (!answer) {
			return
		}

		trackExitEvent(t('exitFeedback.formConsentCheck.reason.label'), answer)

		onSubmit && onSubmit()
	}

	return (
		<form className={styles.form} onSubmit={handleSubmit(onFormSubmit)}>
			<div className={styles.fields}>
				<RadioButtonGroup
					name="exit-reason"
					label={t('exitFeedback.formConsentCheck.reason.label')}
					options={options}
					className={styles.formGroup}
					registerOptions={{ required: t('exitFeedback.fieldRequired') }}
					register={register}
					error={errors['exit-reason'] && t('exitFeedback.fieldRequired')}
				/>
			</div>
			<div className={styles.buttons}>
				{exitButtonSlot}
				<Button className={styles.submit} type="primary" submit={true}>
					{t('exitFeedback.submit')}
				</Button>
			</div>
		</form>
	)
}
